import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../contexts/AuthContext";

interface Props {
  workspaceId: string;
}

interface AnnouncementRow {
  id: string;
  title: string;
  body: string;
  created_at: string;
  ref_id: string | null;
}

const URGENCY_OPTIONS = [
  { value: "normal", label: "일반", color: "#7A9BFF", icon: "ri-notification-line" },
  { value: "important", label: "중요", color: "#FF9F43", icon: "ri-alarm-warning-line" },
  { value: "urgent", label: "긴급", color: "#E8375F", icon: "ri-error-warning-fill" },
];

export default function AnnouncementComposer({ workspaceId }: Props) {
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [urgency, setUrgency] = useState<"normal" | "important" | "urgent">("normal");
  const [sending, setSending] = useState(false);
  const [sentOk, setSentOk] = useState(false);
  const [history, setHistory] = useState<AnnouncementRow[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const loadHistory = useCallback(async () => {
    if (!workspaceId) return;
    setLoadingHistory(true);
    const { data } = await supabase
      .from("role_notifications")
      .select("id, title, body, created_at, ref_id")
      .eq("workspace_id", workspaceId)
      .eq("type", "announcement")
      .order("created_at", { ascending: false })
      .limit(20);
    setHistory((data ?? []) as AnnouncementRow[]);
    setLoadingHistory(false);
  }, [workspaceId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleSend = async () => {
    if (!title.trim() || !body.trim() || !user || !workspaceId) return;
    setSending(true);

    const bodyPayload = JSON.stringify({ text: body, urgency });

    const { error } = await supabase.from("role_notifications").insert({
      workspace_id: workspaceId,
      from_user_id: user.id,
      to_role: "ALL",
      type: "announcement",
      title: title.trim(),
      body: bodyPayload,
      ref_id: urgency,
      is_read: false,
    });

    setSending(false);
    if (!error) {
      setSentOk(true);
      setTitle("");
      setBody("");
      setUrgency("normal");
      await loadHistory();
      setTimeout(() => setSentOk(false), 3000);
    }
  };

  const selectedUrgency = URGENCY_OPTIONS.find(o => o.value === urgency)!;

  return (
    <div className="space-y-5">
      {/* 공지 작성 폼 */}
      <div className="bg-[#111112] rounded-xl border border-white/[0.06] p-5">
        <h3 className="text-[#F2F2F2] font-semibold text-sm mb-4 flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center">
            <i className="ri-megaphone-line text-[#FF9F43] text-base"></i>
          </div>
          전체 공지사항 발송
          <span className="text-[10px] text-[#FF9F43]/50 border border-[#FF9F43]/20 px-1.5 py-0.5 rounded font-normal ml-1">
            학생 인박스 전달
          </span>
        </h3>

        <div className="space-y-4">
          {/* 긴급도 선택 */}
          <div>
            <label className="text-[#555555] text-xs mb-2 block">긴급도</label>
            <div className="flex gap-2">
              {URGENCY_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setUrgency(opt.value as typeof urgency)}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    urgency === opt.value
                      ? "text-white"
                      : "border-white/[0.06] text-[#555555] hover:text-[#888888]"
                  }`}
                  style={urgency === opt.value ? {
                    borderColor: `${opt.color}50`,
                    backgroundColor: `${opt.color}15`,
                    color: opt.color,
                  } : {}}
                >
                  <i className={`${opt.icon} text-sm`}></i>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* 제목 */}
          <div>
            <label className="text-[#555555] text-xs mb-1.5 block">공지 제목</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="공지 제목을 입력하세요..."
              maxLength={100}
              className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-sm rounded-lg px-3 py-2.5 border border-white/[0.08] focus:outline-none focus:border-[#FF9F43]/30 placeholder-[#2A2A2A]"
            />
          </div>

          {/* 내용 */}
          <div>
            <label className="text-[#555555] text-xs mb-1.5 block">공지 내용</label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="학생들에게 전달할 공지 내용을 입력하세요..."
              rows={5}
              maxLength={500}
              className="w-full bg-[#0C0C0E] text-[#CCCCCC] text-sm rounded-lg px-3 py-2.5 border border-white/[0.08] focus:outline-none focus:border-[#FF9F43]/30 placeholder-[#2A2A2A] resize-none"
            />
            <div className="text-right text-[#3D3D3D] text-[10px] mt-1">{body.length}/500</div>
          </div>

          {/* 미리보기 */}
          {title && body && (
            <div
              className="rounded-lg border px-4 py-3 text-xs space-y-1.5"
              style={{
                borderColor: `${selectedUrgency.color}25`,
                backgroundColor: `${selectedUrgency.color}06`,
              }}
            >
              <div className="text-[#3D3D3D] text-[9px] uppercase tracking-widest mb-1">발송 미리보기</div>
              <div className="flex items-center gap-2">
                <i className={`${selectedUrgency.icon} text-sm`} style={{ color: selectedUrgency.color }}></i>
                <span className="font-semibold" style={{ color: selectedUrgency.color }}>
                  [{selectedUrgency.label}] {title}
                </span>
              </div>
              <p className="text-[#555555] text-[10px] leading-relaxed whitespace-pre-wrap">{body}</p>
              <div className="text-[#3D3D3D] text-[9px] pt-1 border-t border-white/[0.04]">
                대상: 이 워크스페이스의 전체 학생 · 학생 인박스 &quot;공지&quot; 탭에 표시됩니다
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={handleSend}
            disabled={!title.trim() || !body.trim() || sending || !workspaceId}
            className={`w-full py-3 rounded-xl font-semibold text-sm transition-all whitespace-nowrap ${
              sentOk
                ? "bg-emerald-500 text-white cursor-default"
                : !title.trim() || !body.trim() || !workspaceId
                  ? "bg-white/[0.04] text-[#2A2A2A] cursor-not-allowed"
                  : "bg-[#FF9F43] text-white cursor-pointer hover:bg-[#FFB060]"
            }`}
          >
            {sending
              ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin"></i>발송 중...</span>
              : sentOk
                ? <span className="flex items-center justify-center gap-2"><i className="ri-checkbox-circle-fill"></i>전체 학생에게 발송 완료!</span>
                : <span className="flex items-center justify-center gap-2"><i className="ri-send-plane-line"></i>전체 발송</span>
            }
          </button>
        </div>
      </div>

      {/* 발송 이력 */}
      <div className="bg-[#111112] rounded-xl border border-white/[0.06] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/[0.05] flex items-center justify-between">
          <span className="text-[#888888] text-xs font-semibold">발송된 공지 이력</span>
          <button type="button" onClick={loadHistory}
            className="w-6 h-6 flex items-center justify-center text-[#3D3D3D] hover:text-[#777777] cursor-pointer transition-colors">
            <i className={`ri-refresh-line text-xs ${loadingHistory ? "animate-spin" : ""}`}></i>
          </button>
        </div>

        {loadingHistory ? (
          <div className="px-5 py-8 text-center text-[#3D3D3D] animate-pulse text-xs">로딩 중...</div>
        ) : history.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <i className="ri-megaphone-line text-2xl text-[#222222] block mb-3"></i>
            <p className="text-[#3D3D3D] text-xs">발송된 공지가 없어요</p>
          </div>
        ) : (
          <div className="divide-y divide-white/[0.03]">
            {history.map(item => {
              let parsedText = "";
              let parsedUrgency = "normal";
              try {
                const p = JSON.parse(item.body);
                parsedText = p.text ?? item.body;
                parsedUrgency = p.urgency ?? item.ref_id ?? "normal";
              } catch {
                parsedText = item.body;
                parsedUrgency = item.ref_id ?? "normal";
              }
              const urg = URGENCY_OPTIONS.find(o => o.value === parsedUrgency) ?? URGENCY_OPTIONS[0];
              return (
                <div key={item.id} className="px-5 py-3">
                  <div className="flex items-start gap-2 mb-1">
                    <i className={`${urg.icon} text-sm shrink-0 mt-0.5`} style={{ color: urg.color }}></i>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[#CCCCCC] text-xs font-medium truncate">{item.title}</span>
                        <span className="text-[#3D3D3D] text-[9px] shrink-0 tabular-nums">
                          {new Date(item.created_at).toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" })}
                          {" "}
                          {new Date(item.created_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                      <p className="text-[#555555] text-[10px] mt-0.5 leading-relaxed line-clamp-2 whitespace-pre-wrap">
                        {parsedText}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5 pl-5">
                    <span
                      className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full border"
                      style={{ color: urg.color, borderColor: `${urg.color}30`, backgroundColor: `${urg.color}10` }}
                    >
                      {urg.label}
                    </span>
                    <span className="text-[#3D3D3D] text-[9px]">전체 학생 발송</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
